/** Shared API auth — user session token (preferred) or legacy EXPECTED_TOKEN. */

const ENV_TOKEN = (process.env.REACT_APP_API_TOKEN || '').trim();
const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || 'token').toLowerCase();
const AUTH_HEADER_NAME = (process.env.REACT_APP_AUTH_HEADER_NAME || '').trim();
/** Default false — token only in headers, never in URL (avoids leaking in logs/history). */
const TOKEN_IN_QUERY =
  (process.env.REACT_APP_TOKEN_IN_QUERY || 'false').trim().toLowerCase() === 'true';
/** When true (default), App requires email/password login instead of ApiTokenBar alone. */
const REQUIRE_USER_LOGIN =
  (process.env.REACT_APP_REQUIRE_USER_LOGIN || 'true').trim().toLowerCase() !== 'false';

const STORAGE_KEY = 'godo_api_token';
const SESSION_STORAGE_KEY = 'godo_session_token';
export const TOKEN_CHANGED_EVENT = 'godo-token-changed';
export const SESSION_KICKED_EVENT = 'godo-session-kicked';

/** Email OTP / authenticator codes — ignore spaces copied from the email. */
export function digitsOnly(value, maxLen = 6) {
  return String(value || '')
    .replace(/\D/g, '')
    .slice(0, maxLen);
}

/** Do not set HTML maxLength={6} — spaced email codes ("2 2 7 …") get truncated to 3 digits. */
export function otpFieldHandlers(setter) {
  return {
    inputMode: 'numeric',
    autoComplete: 'one-time-code',
    maxLength: 32,
    onChange: (e) => setter(digitsOnly(e.target.value)),
    onPaste: (e) => {
      const text = e.clipboardData?.getData('text') ?? '';
      const digits = digitsOnly(text);
      if (!digits) return;
      e.preventDefault();
      setter(digits);
    },
  };
}

/** In-memory overrides. */
let sessionToken = null;
let userSessionToken = null;

export function requireUserLogin() {
  return REQUIRE_USER_LOGIN;
}

export function getUserSessionToken() {
  if (userSessionToken) return userSessionToken;
  try {
    const stored = (localStorage.getItem(SESSION_STORAGE_KEY) || '').trim();
    if (stored) return stored;
  } catch {
    /* private browsing */
  }
  return '';
}

export function setUserSessionToken(token) {
  const next = (token || '').trim();
  userSessionToken = next || null;
  try {
    if (next) {
      localStorage.setItem(SESSION_STORAGE_KEY, next);
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(TOKEN_CHANGED_EVENT));
}

export function clearUserSession() {
  setUserSessionToken('');
}

/** Prefer user session, then legacy API token paste/env. */
export function getApiToken() {
  const userTok = getUserSessionToken();
  if (userTok) return userTok;
  if (sessionToken) return sessionToken;
  try {
    const stored = (localStorage.getItem(STORAGE_KEY) || '').trim();
    if (stored) return stored;
  } catch {
    /* private browsing */
  }
  return ENV_TOKEN;
}

export function isEnvApiTokenConfigured() {
  return Boolean(ENV_TOKEN);
}

export function shouldShowApiTokenBar() {
  if (REQUIRE_USER_LOGIN) return false;
  const forceShow =
    (process.env.REACT_APP_SHOW_API_TOKEN_UI || '').trim().toLowerCase() === 'true';
  if (forceShow) return true;
  return !ENV_TOKEN;
}

export function setApiToken(token) {
  const next = (token || '').trim();
  sessionToken = next || null;
  try {
    if (next) {
      localStorage.setItem(STORAGE_KEY, next);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(TOKEN_CHANGED_EVENT));
}

export function clearApiToken() {
  setApiToken('');
}

export function shouldPutTokenInQuery() {
  return TOKEN_IN_QUERY;
}

export function buildAuthHeaders(extra = {}) {
  const headers = { Accept: 'application/json', ...extra };
  const apiToken = getApiToken();
  if (!apiToken) return headers;

  headers.token = apiToken;
  // Always send Bearer so session tokens work with Authorization parsers.
  headers.Authorization = `Bearer ${apiToken}`;

  if (AUTH_MODE === 'bearer' || AUTH_HEADER_NAME) {
    /* already set Authorization */
  }

  if (AUTH_HEADER_NAME) {
    headers[AUTH_HEADER_NAME] = apiToken;
  }

  return headers;
}

export function appendTokenSearchParams(params) {
  const apiToken = getApiToken();
  if (apiToken && TOKEN_IN_QUERY) {
    params.set('token', apiToken);
  }
  return params;
}

export function isDashboardSameOriginHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === 'uat-powerbi.godocm.com' ||
    host === 'riskmanagement.logz.com'
  );
}

export function normalizeApiBase(raw = process.env.REACT_APP_API_BASE || '') {
  if (isDashboardSameOriginHost()) return '';
  const trimmed = String(raw).trim().replace(/\/$/, '');
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const API_BASE = normalizeApiBase();

export function apiUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${p}`;
}

async function readJson(res) {
  return res.json().catch(() => ({}));
}

export async function fetchAuthBootstrap() {
  const res = await fetch(apiUrl('/api/auth/bootstrap'), {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const err = await readJson(res);
    throw new Error(formatApiError(err, res.status));
  }
  return res.json();
}

export async function createAuthUser({ email, password, is_admin = false }) {
  const res = await fetch(apiUrl('/api/auth/users'), {
    method: 'POST',
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ email, password, is_admin }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function loginStart({ email, password }) {
  const res = await fetch(apiUrl('/api/auth/login'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function loginChooseMethod({ challenge_id, method }) {
  const res = await fetch(apiUrl('/api/auth/login/method'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_id, method }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function loginTotpSetup({ challenge_id }) {
  const res = await fetch(apiUrl('/api/auth/login/totp/setup'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_id }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function loginTotpEnable({ challenge_id, code, email, password }) {
  const res = await fetch(apiUrl('/api/auth/login/totp/enable'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({
      challenge_id,
      code: digitsOnly(code),
      email,
      password,
    }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function loginTotp({ challenge_id, code }) {
  const res = await fetch(apiUrl('/api/auth/login/totp'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_id, code: digitsOnly(code) }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  if (body.session_token) {
    setUserSessionToken(body.session_token);
  }
  return body;
}

export async function loginOtp({ challenge_id, code }) {
  const res = await fetch(apiUrl('/api/auth/login/otp'), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ challenge_id, code: digitsOnly(code) }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  if (body.session_token) {
    setUserSessionToken(body.session_token);
  }
  return body;
}

export async function logoutSession() {
  try {
    await fetch(apiUrl('/api/auth/logout'), {
      method: 'POST',
      headers: buildAuthHeaders(),
    });
  } catch {
    /* ignore */
  }
  clearUserSession();
}

export async function fetchAuthSession() {
  const tok = getUserSessionToken();
  if (!tok) {
    const err = new Error('Not authenticated');
    err.status = 401;
    throw err;
  }
  const res = await fetch(apiUrl('/api/auth/session'), {
    headers: buildAuthHeaders(),
  });
  const body = await readJson(res);
  if (res.status === 401) {
    clearUserSession();
    window.dispatchEvent(
      new CustomEvent(SESSION_KICKED_EVENT, {
        detail: {
          message:
            'Another person signed in to this account — please log in again.',
        },
      })
    );
    const err = new Error(formatApiError(body, 401));
    err.status = 401;
    throw err;
  }
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function totpSetup() {
  const res = await fetch(apiUrl('/api/auth/totp/setup'), {
    method: 'POST',
    headers: buildAuthHeaders(),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function totpEnable(code) {
  const res = await fetch(apiUrl('/api/auth/totp/enable'), {
    method: 'POST',
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ code: digitsOnly(code) }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

export async function totpDisable({ password, code }) {
  const res = await fetch(apiUrl('/api/auth/totp/disable'), {
    method: 'POST',
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ password, code: digitsOnly(code) }),
  });
  const body = await readJson(res);
  if (!res.ok) throw new Error(formatApiError(body, res.status));
  return body;
}

/** Check token against pricing FastAPI (no MT5 session). */
export async function verifyApiToken(tokenOverride) {
  const apiToken = (tokenOverride ?? getApiToken()).trim();
  if (!apiToken) {
    throw new Error('Enter API token first');
  }

  const headers = {
    Accept: 'application/json',
    token: apiToken,
    Authorization: `Bearer ${apiToken}`,
  };
  const verifyUrl = `${API_BASE}/api/auth/verify`;
  let res = await fetch(verifyUrl, { headers });

  if (res.status === 404) {
    res = await fetch(`${API_BASE}/api/pricing/last?symbol=EURUSD`, { headers });
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatApiError(err, res.status));
  }
  return res.json();
}

export function formatApiError(errBody, status) {
  const gatewayMsg =
    typeof errBody?.message === 'string' ? errBody.message : null;
  const detail = errBody?.detail;
  const fastapiMsg = Array.isArray(detail)
    ? detail.map((d) => d.msg).join(', ')
    : typeof detail === 'string'
      ? detail
      : null;
  const msg = fastapiMsg || gatewayMsg || `API error ${status}`;

  if (status === 401) {
    if (gatewayMsg?.includes('Auth Token')) {
      return (
        `${msg} — this host is the PowerBI JWT gateway, not the pricing FastAPI service. ` +
        'Point REACT_APP_API_BASE to the same host as /ws/demo (pricing service), ' +
        'or set REACT_APP_API_TOKEN in websocket/.env.'
      );
    }
    if (
      /login expired|challenge expired|invalid or expired otp|invalid authenticator|invalid password|complete password/i.test(
        msg
      )
    ) {
      return msg;
    }
    return `${msg} — check login session or API token`;
  }
  return msg;
}
